import React from "react";
import { getFormInitialValues, reduxForm, formValueSelector } from "redux-form";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import * as PropTypes from "prop-types";
import { PrimaryButton } from "../../../../common/Button";
import buttonMessages from "../../../../../intl/common/buttonMessages";
import {
    BUTTON_LABEL_CLOSE,
    BUTTON_LABEL_REOPEN,
    FORM_BUTTON_CLOSE,
    FORM_BUTTON_REOPEN,
    FORM_BUTTON_SAVE,
    FORM_BUTTON_TRANSFER_TO_REQUESTOR,
    BUTTON_LABEL_TRANSFER_TO_REQUESTOR,
    FORM_BUTTON_ESCALATE,
    BUTTON_LABEL_ESCALATE,
    FORM_BUTTON_ANSWER,
    BUTTON_LABEL_ANSWER,
    FORM_BUTTON_EDIT,
    FORM_BUTTON_TAKE_BACK,
    BUTTON_LABEL_TAKE_BACK,
    ASE_INTERNAL_TICKET_STATUS_CLOSED,
    ASE_INTERNAL_TICKET_STATUS_TRANSFERRED,
    ASE_INTERNAL_TICKET_STATUS_ANSWERED,
    ASE_INTERNAL_TICKET_STATUS_REOPENED,
    BUTTON_LABEL_SAVE_AND_SEND,
    BUTTON_LABEL_SAVE,
    FORM_STATUS_HISTORY_SECTION,
    ASE_INTERNAL_TICKET_FORM,
} from "../../../../../constants/formConstants";
import { RequiredSpan } from "../../../../common/StyledComponents";
import { withRouter } from "react-router-dom";
import get from "get-value";
import ValidFormattedMessage from "../../../../common/ValidFormattedMessage";
import commonMessages from "../../../../../intl/common/commonMessages";
import { editAseInternalTicketListButtonVisibility } from "../../../../../constants/aseInternalTicketRights";
import { moduleRoles } from "../../../../../utils/roles";
import validationMessages from "../../../../../intl/common/validationMessages";

const SubmitButtons = ({
    handleSubmit,
    submitting,
    initialValues,
    readOnly,
    userRights,
    dirty,
    escalationSGs = [],
    answeringSGs = [],
    history,
    onSubmit,
    roles,
    ticketSG,
    canTakeBack,
    ticketHistory = [],
    userDetail,
}) => {
    const ticketStatus = get(initialValues, "ticketSection.status");

    const filteredAnsweringSGs = answeringSGs.filter((sg) =>
        ticketHistory.some(
            (historySg) => get(historySg, "toSolutionGroup.id") === sg.id,
        ),
    );
    const escalationSg = escalationSGs[0];
    const answeringSg = filteredAnsweringSGs[0];
    const [selectedGroupForEscalate, setSelectedGroupForEscalate] =
        React.useState(escalationSg);
    const [selectedGroupForAnswer, setSelectedGroupForAnswer] =
        React.useState(answeringSg);
    React.useEffect(() => {
        setSelectedGroupForEscalate(escalationSg);
    }, [escalationSg]);
    React.useEffect(() => {
        setSelectedGroupForAnswer(answeringSg);
    }, [answeringSg]);

    const effectiveEscalationSg = selectedGroupForEscalate || escalationSg;
    const effectiveAnswerSg = selectedGroupForAnswer || answeringSg;

    const clickLockRef = React.useRef(false);

    const statusClosed = ticketStatus === ASE_INTERNAL_TICKET_STATUS_CLOSED;

    const canEscalateToSG = userRights.canEscalate && !!escalationSGs.length;
    const canAnswerToSG = userRights.canAnswer && !!filteredAnsweringSGs.length;

    const renderCancelButton = () => {
        return (
            <PrimaryButton
                onClick={(e) => {
                    e.preventDefault();
                    history.goBack();
                }}
                className="btn mr-2 float-right"
            >
                <FormattedMessage {...buttonMessages.CANCEL} />
            </PrimaryButton>
        );
    };

    const renderBackButton = () => {
        return (
            <PrimaryButton
                onClick={(e) => {
                    e.preventDefault();
                    history.goBack();
                }}
                className="btn mr-2 float-right"
            >
                <FormattedMessage {...buttonMessages.BACK} />
            </PrimaryButton>
        );
    };

    const renderEditButton = () => {
        return (
            <PrimaryButton
                onClick={handleSubmit((values) =>
                    onSubmit({
                        submitBy: FORM_BUTTON_EDIT,
                    }),
                )}
                className="btn mr-2 float-right"
            >
                <FormattedMessage {...buttonMessages.EDIT} />
            </PrimaryButton>
        );
    };

    const renderButton = (
        button,
        message,
        changeToStatus,
        changeToSolutionGroup,
    ) => {
        return (
            <PrimaryButton
                onClick={(e) => {
                    e.preventDefault();
                    if (clickLockRef.current) return;
                    clickLockRef.current = true;
                    setTimeout(() => {
                        clickLockRef.current = false;
                    }, 1000);
                    //Unfortunately haven't found any more elegant way to omit validation on MARK_AS_READ button click, this value is checked in REQUIRED validation
                    handleSubmit((values) =>
                        onSubmit({
                            newValues: {
                                ...values,
                                ticketSection: {
                                    ...values.ticketSection,
                                    status: changeToStatus,
                                    ...(button === FORM_BUTTON_ESCALATE && {
                                        assignedSolutionGroup: {
                                            id: effectiveEscalationSg.id,
                                            name: effectiveEscalationSg.name,
                                        },
                                    }),
                                    ...(button === FORM_BUTTON_ANSWER && {
                                        assignedSolutionGroup: {
                                            id: effectiveAnswerSg.id,
                                            name: effectiveAnswerSg.name,
                                        },
                                    }),
                                    ...(button ===
                                        FORM_BUTTON_TRANSFER_TO_REQUESTOR && {
                                        assignedSolutionGroup: {
                                            id: changeToSolutionGroup.id,
                                            name: changeToSolutionGroup.name,
                                        },
                                    }),
                                    ...(button === FORM_BUTTON_TAKE_BACK && {
                                        assignedSolutionGroup: {
                                            id: changeToSolutionGroup.id,
                                            name: changeToSolutionGroup.name,
                                        },
                                    }),
                                },
                            },
                            oldValues: initialValues,
                            submitBy: button,
                        }),
                    )();
                }}
                className="btn mr-2 float-right"
                disabled={submitting || (!dirty && button === FORM_BUTTON_SAVE)}
            >
                <ValidFormattedMessage
                    message={message}
                    intlMessages={buttonMessages}
                />
            </PrimaryButton>
        );
    };

    const renderSelectButton = (
        button,
        message,
        changeToStatus,
        availableOptions,
        value,
        setValue,
    ) => {
        const moveLeft = { marginLeft: -10, marginRight: 10 };
        return (
            <>
                <div className="input-group-prepend">
                    {renderButton(button, message, changeToStatus)}
                </div>
                <select
                    className="custom-select"
                    value={value}
                    onChange={setValue}
                    style={moveLeft}
                >
                    {availableOptions.map((item) => (
                        <option key={item.key} value={item.key}>
                            {item.value}
                        </option>
                    ))}
                </select>
            </>
        );
    };

    const renderTransferToRequestorButton = () => {
        const requestorSolutionGroup = get(
            initialValues,
            "requestorSolutionGroup",
            {},
        );
        return renderButton(
            FORM_BUTTON_TRANSFER_TO_REQUESTOR,
            BUTTON_LABEL_TRANSFER_TO_REQUESTOR,
            ASE_INTERNAL_TICKET_STATUS_ANSWERED,
            requestorSolutionGroup,
        );
    };

    const canAnswerToRequestor = () => {
        const isAssignedToSgL3 = get(
            initialValues,
            "ticketSection.assignedSolutionGroup.name",
            "",
        )
            .toLowerCase()
            .includes("l3");
        const isRequestorSgInL1 =
            get(initialValues, "requestorSolutionGroup.name", "")
                .toLowerCase()
                .includes("l1") ||
            get(initialValues, "ticketSection.solutionGroup.name", "")
                .toLowerCase()
                .includes("l1");
        const requestorGroupId = get(
            initialValues,
            "requestorSolutionGroup.id",
            "",
        );
        const assignedGroupId = get(
            initialValues,
            "ticketSection.assignedSolutionGroup.id",
            "",
        );
        return (
            requestorGroupId &&
            assignedGroupId &&
            requestorGroupId !== assignedGroupId &&
            answeringSGs.some((sg) => sg.id === requestorGroupId) &&
            !(isAssignedToSgL3 && isRequestorSgInL1)
        );
    };

    const canClose = () => {
        const requestorGroupId = get(
            initialValues,
            "requestorSolutionGroup.id",
            "",
        );
        const assignedGroupId = get(
            initialValues,
            "ticketSection.assignedSolutionGroup.id",
            "",
        );
        const requestorIpn = get(
            initialValues,
            "ticketSection.requestorIpn",
            "",
        );
        const isAdmin =
            moduleRoles.isAdminAse(roles) || moduleRoles.isSGAdminAse(roles);
        const isRequestor = userDetail.ipn === requestorIpn;
        return (
            userRights.canClose &&
            requestorGroupId === assignedGroupId &&
            (isAdmin || isRequestor)
        );
    };

    const renderReopenButton = () => {
        return renderButton(
            FORM_BUTTON_REOPEN,
            BUTTON_LABEL_REOPEN,
            ASE_INTERNAL_TICKET_STATUS_REOPENED,
        );
    };

    const renderEscalateButton = () =>
        renderSelectButton(
            FORM_BUTTON_ESCALATE,
            BUTTON_LABEL_ESCALATE,
            ASE_INTERNAL_TICKET_STATUS_TRANSFERRED,
            escalationSGs.map((item) => ({ key: item.id, value: item.name })),
            effectiveEscalationSg.id,
            (e) =>
                setSelectedGroupForEscalate(
                    escalationSGs.find((sg) => sg.id === e.target.value),
                ),
        );

    const renderAnswerButton = () =>
        renderSelectButton(
            FORM_BUTTON_ANSWER,
            BUTTON_LABEL_ANSWER,
            ASE_INTERNAL_TICKET_STATUS_ANSWERED,
            filteredAnsweringSGs.map((item) => ({
                key: item.id,
                value: item.name,
            })),
            effectiveAnswerSg.id,
            (e) =>
                setSelectedGroupForAnswer(
                    filteredAnsweringSGs.find((sg) => sg.id === e.target.value),
                ),
        );

    const renderTakeBackButton = () => {
        const type = get(canTakeBack, "takeBackType", "");
        return renderButton(
            FORM_BUTTON_TAKE_BACK,
            BUTTON_LABEL_TAKE_BACK,
            type === "ANSWER"
                ? ASE_INTERNAL_TICKET_STATUS_ANSWERED
                : ASE_INTERNAL_TICKET_STATUS_TRANSFERRED,
            canTakeBack,
        );
    };

    const ticketInNonEditableState = () => {
        const buttonVisibility =
            editAseInternalTicketListButtonVisibility.AS_SG_EDITOR;
        return !buttonVisibility.includes(ticketStatus);
    };

    if (readOnly) {
        let canSwitchToEdit =
            !statusClosed &&
            !ticketInNonEditableState() &&
            moduleRoles.isSGEditorAseInSG(
                roles,
                get(ticketSG, "id", { default: "" }),
            );

        return (
            <div className="d-flex justify-content-end mt-3">
                <form className="form-group">
                    {renderBackButton()}
                    {canSwitchToEdit && renderEditButton()}
                    {canTakeBack && renderTakeBackButton()}
                    {userRights.canReopen &&
                        statusClosed &&
                        renderReopenButton()}
                </form>
            </div>
        );
    }

    return (
        <div className="d-flex justify-content-between mt-3">
            <div className="flex-column">
                <div>
                    <RequiredSpan>{"*\u00A0"}</RequiredSpan>
                    <FormattedMessage {...commonMessages.IS_MANDATORY} />
                </div>
                {!ticketStatus && !canEscalateToSG && (
                    <div>
                        <RequiredSpan>
                            <FormattedMessage
                                {...validationMessages.INTERNAL_TICKET_CREATION_ESCALATION}
                            />
                        </RequiredSpan>
                    </div>
                )}
            </div>
            <form>
                <div className="input-group">
                    {renderBackButton()}
                    {userRights.canCancel && renderCancelButton()}
                    {canEscalateToSG && renderEscalateButton()}
                    {canAnswerToSG && renderAnswerButton()}
                    {userRights.canAnswerToRequestor &&
                        canAnswerToRequestor() &&
                        renderTransferToRequestorButton()}
                    {canClose() &&
                        renderButton(
                            FORM_BUTTON_CLOSE,
                            BUTTON_LABEL_CLOSE,
                            ASE_INTERNAL_TICKET_STATUS_CLOSED,
                        )}
                    {userRights.canSave &&
                        renderButton(
                            FORM_BUTTON_SAVE,
                            ticketStatus
                                ? BUTTON_LABEL_SAVE
                                : BUTTON_LABEL_SAVE_AND_SEND,
                            ticketStatus,
                        )}
                </div>
            </form>
        </div>
    );
};

function getSolutionGroups(
    aseInternalTicketState,
    fullState,
    schemaType,
    form,
) {
    if (
        !aseInternalTicketState.solutionGroupInfo ||
        !aseInternalTicketState.solutionGroupInfo[schemaType]
    ) {
        return undefined;
    }
    const initialValues = getFormInitialValues(form)(fullState);
    const docType =
        get(initialValues, "ticketSection.formType") ||
        get(aseInternalTicketState, "ticket.formType") ||
        ASE_INTERNAL_TICKET_FORM;
    return aseInternalTicketState.solutionGroupInfo[schemaType][docType];
}

SubmitButtons.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    readOnly: PropTypes.any,
    submitting: PropTypes.bool.isRequired,
    dirty: PropTypes.bool,
    initialValues: PropTypes.object.isRequired,
    userRights: PropTypes.object.isRequired,
    roles: PropTypes.object.isRequired,
    ticketSG: PropTypes.any,
    canTakeBack: PropTypes.any,
};

const selector = (form, ...other) => formValueSelector(form)(...other);
const mapStateToProps = (state, initialProps) => ({
    ticketHistory: selector(
        initialProps.form,
        state,
        `${FORM_STATUS_HISTORY_SECTION}`,
    ),
    initialValues: getFormInitialValues(initialProps.form)(state),
    roles: state.profile.userDetail.roles,
    escalationSGs: getSolutionGroups(
        state.aseInternalTicket,
        state,
        "escalationSchemas",
        initialProps.form,
    ),
    answeringSGs: getSolutionGroups(
        state.aseInternalTicket,
        state,
        "answeringSchemas",
        initialProps.form,
    ),
    ticketSG: get(state, "aseInternalTicket.ticket.assignedSolutionGroup"),
    canTakeBack: get(state, "aseInternalTicket.canTakeBack"),
    userDetail: state.profile.userDetail,
});

export default reduxForm({})(
    withRouter(connect(mapStateToProps, {})(SubmitButtons)),
);

